import React, {
  useContext,
  useEffect,
  useReducer,
  useState,
  useRef,
} from "react";
import { Link, useLocation } from "react-router-dom";
import useHelps from "../../hooks/useHelps";
import Tooltip from "@material-ui/core/Tooltip";

import {
  BarChart3,
  MessageSquare,
  Zap,
  Columns,
  User,
  Clock,
  Tag,
  MessageCircle,
  HelpCircle,
  Settings,
  Users,
  ListOrdered,
  ArrowLeftRight,
  Server,
  Paperclip,
  DollarSign,
  Building2,
  ChevronDown,
  FileText,
  Table,
  List,
  UsersRound,
  Megaphone,
  MessagesSquare,
  Info,
  Code,
  Sparkles,
  Cable,
  Home,
  Cog,
  AlertTriangle,
  Wifi,
  WifiOff,
  Cake,
  Image,
  Phone,
  Wallet,
  Webhook,
  Workflow,
  Calendar,
  History,
  SlidersHorizontal,
  Bell,
} from "lucide-react";

import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useActiveMenu } from "../../context/ActiveMenuContext";
import ColorModeContext from "../themeContext";

import { Can } from "../../components/Can";

import { isArray } from "lodash";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import usePlans from "../../hooks/usePlans";
import useVersion from "../../hooks/useVersion";
import { i18n } from "../../translate/i18n";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import "./mainListItems.css";

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

const CanWrapper = ({ role, perform, children }) => {
  return (
    <Can
      role={role}
      perform={perform}
      yes={() => (
        <div
          className="can-wrapper"
          style={{ width: "100%", display: "block" }}
        >
          {children}
        </div>
      )}
      no={() => null}
    />
  );
};

const MainListItems = ({ isMobile = false, closeDrawer }) => {
  const colorModeContext = useContext(ColorModeContext);
  const isDarkMode = colorModeContext.mode === "dark";
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);
  const { setActiveMenu } = useActiveMenu();
  const location = useLocation();

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showKanbanEnterprise, setShowKanbanEnterprise] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);
  const [showWavoipCall, setShowWavoipCall] = useState(false);
  const [showGroupManagement, setShowGroupManagement] = useState(false);
  const [showPhraseListFlow, setShowPhraseListFlow] = useState(true);
  const [showConversationFlow, setShowConversationFlow] = useState(true);
  const [showFollowUp, setShowFollowUp] = useState(true);

  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const [version, setVersion] = useState(false);
  const { list } = useHelps();
  const [hasHelps, setHasHelps] = useState(false);
  const [showWallets, setShowWallets] = useState(false);

  const [managementMenuOpen, setManagementMenuOpen] = useState(false);
  const [ticketsMenuOpen, setTicketsMenuOpen] = useState(false);
  const [campaignsMenuOpen, setCampaignsMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [flowMenuOpen, setFlowMenuOpen] = useState(false);

  const managementMenuRef = useRef(null);
  const managementButtonRef = useRef(null);
  const ticketsMenuRef = useRef(null);
  const ticketsButtonRef = useRef(null);
  const campaignsMenuRef = useRef(null);
  const campaignsButtonRef = useRef(null);
  const adminMenuRef = useRef(null);
  const adminButtonRef = useRef(null);
  const flowMenuRef = useRef(null);
  const flowButtonRef = useRef(null);

  const { get: getSetting } = useCompanySettings();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const setting = await getSetting({
          column: "DirectTicketsToWallets",
        });
        setShowWallets(setting.DirectTicketsToWallets);
      } catch (err) {
        // Silent fail
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (isMobile) {
      function handleClickOutside(event) {
        if (
          managementMenuOpen &&
          managementButtonRef.current &&
          !managementButtonRef.current.contains(event.target) &&
          managementMenuRef.current &&
          !managementMenuRef.current.contains(event.target)
        ) {
          setManagementMenuOpen(false);
        }

        if (
          ticketsMenuOpen &&
          ticketsButtonRef.current &&
          !ticketsButtonRef.current.contains(event.target) &&
          ticketsMenuRef.current &&
          !ticketsMenuRef.current.contains(event.target)
        ) {
          setTicketsMenuOpen(false);
        }

        if (
          campaignsMenuOpen &&
          campaignsButtonRef.current &&
          !campaignsButtonRef.current.contains(event.target) &&
          campaignsMenuRef.current &&
          !campaignsMenuRef.current.contains(event.target)
        ) {
          setCampaignsMenuOpen(false);
        }

        if (
          adminMenuOpen &&
          adminButtonRef.current &&
          !adminButtonRef.current.contains(event.target) &&
          adminMenuRef.current &&
          !adminMenuRef.current.contains(event.target)
        ) {
          setAdminMenuOpen(false);
        }

        if (
          flowMenuOpen &&
          flowButtonRef.current &&
          !flowButtonRef.current.contains(event.target) &&
          flowMenuRef.current &&
          !flowMenuRef.current.contains(event.target)
        ) {
          setFlowMenuOpen(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [
    managementMenuOpen,
    ticketsMenuOpen,
    campaignsMenuOpen,
    adminMenuOpen,
    flowMenuOpen,
    isMobile,
  ]);

  const closeAllMenus = () => {
    setManagementMenuOpen(false);
    setTicketsMenuOpen(false);
    setCampaignsMenuOpen(false);
    setAdminMenuOpen(false);
    setFlowMenuOpen(false);

    if (isMobile && closeDrawer) {
      closeDrawer();
    }
  };

  const handleMenuItemClick = (to) => {
    if (isMobile && closeDrawer) {
      closeDrawer();
    }
    closeAllMenus();
  };

  const toggleManagementMenu = (e) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();
      setManagementMenuOpen(!managementMenuOpen);
      setTicketsMenuOpen(false);
      setCampaignsMenuOpen(false);
      setAdminMenuOpen(false);
      setFlowMenuOpen(false);
    }
  };

  const toggleTicketsMenu = (e) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();
      setTicketsMenuOpen(!ticketsMenuOpen);
      setManagementMenuOpen(false);
      setCampaignsMenuOpen(false);
      setAdminMenuOpen(false);
      setFlowMenuOpen(false);
    }
  };

  const toggleCampaignsMenu = (e) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();
      setCampaignsMenuOpen(!campaignsMenuOpen);
      setManagementMenuOpen(false);
      setTicketsMenuOpen(false);
      setAdminMenuOpen(false);
      setFlowMenuOpen(false);
    }
  };

  const toggleAdminMenu = (e) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();
      setAdminMenuOpen(!adminMenuOpen);
      setManagementMenuOpen(false);
      setTicketsMenuOpen(false);
      setCampaignsMenuOpen(false);
      setFlowMenuOpen(false);
    }
  };

  const toggleFlowMenu = (e) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();
      setFlowMenuOpen(!flowMenuOpen);
      setManagementMenuOpen(false);
      setTicketsMenuOpen(false);
      setCampaignsMenuOpen(false);
      setAdminMenuOpen(false);
    }
  };

  const isManagementActive =
    location.pathname === "/" ||
    location.pathname.startsWith("/reports") ||
    location.pathname.startsWith("/moments") ||
    location.pathname.startsWith("/plugins/floup/dashboard") ||
    location.pathname.startsWith("/wallets");

  const isTicketsActive =
    location.pathname.startsWith("/tickets") ||
    location.pathname.startsWith("/browser-notifications") ||
    location.pathname.startsWith("/quick-messages") ||
    location.pathname.startsWith("/whatsapp-template-create") ||
    location.pathname.startsWith("/whatsapp-templates") ||
    location.pathname.startsWith("/kanban") ||
    location.pathname.startsWith("/contacts") ||
    location.pathname.startsWith("/schedules") ||
    location.pathname.startsWith("/tags") ||
    location.pathname.startsWith("/custom-contact-fields") ||
    location.pathname.startsWith("/chats") ||
    location.pathname.startsWith("/helps") ||
    location.pathname.startsWith("/status") ||
    location.pathname.startsWith("/call-historicals");

  const isCampaignRouteActive =
    location.pathname === "/campaigns" ||
    location.pathname.startsWith("/contact-lists") ||
    location.pathname.startsWith("/campaigns-config") ||
    location.pathname.startsWith("/official-campaigns") ||
    location.pathname.startsWith("/campaigns-pro");

  const isFlowRouteActive =
    location.pathname.startsWith("/phrase-lists") ||
    location.pathname.startsWith("/flowbuilders") ||
    location.pathname.startsWith("/flowbuilder") ||
    location.pathname.startsWith("/plugins/floup");

  const isAdminActive =
    location.pathname.startsWith("/users") ||
    location.pathname.startsWith("/queues") ||
    location.pathname.startsWith("/prompts") ||
    location.pathname.startsWith("/queue-integration") ||
    location.pathname.startsWith("/connections") ||
    location.pathname.startsWith("/allConnections") ||
    location.pathname.startsWith("/files") ||
    location.pathname.startsWith("/financeiro") ||
    location.pathname.startsWith("/settings") ||
    location.pathname.startsWith("/companies") ||
    location.pathname.startsWith("/announcements") ||
    location.pathname.startsWith("/messages-api") ||
    location.pathname.startsWith("/api-oficial-docs") ||
    location.pathname.startsWith("/test-interactive") ||
    location.pathname.startsWith("/birthday-settings") ||
    location.pathname.startsWith("/holidays") ||
    location.pathname.startsWith("/audit-logs");

  const { getPlanCompany } = usePlans();
  const { getVersion } = useVersion();

  useEffect(() => {
    async function fetchVersion() {
      const _version = await getVersion();
      setVersion(_version.version);
    }
    fetchVersion();
  }, []);

  useEffect(() => {
    async function checkHelps() {
      const helps = await list();
      setHasHelps(helps.length > 0);
    }
    checkHelps();
  }, []);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      if (!companyId) return;

      try {
        const planConfigs = await getPlanCompany(undefined, companyId);

        if (planConfigs && planConfigs.plan) {
          setShowCampaigns(planConfigs.plan.useCampaigns);
          setShowKanban(planConfigs.plan.useKanban);
          setShowKanbanEnterprise(planConfigs.plan.useKanbanEnterprise);
          setShowOpenAi(planConfigs.plan.useOpenAi);
          setShowIntegrations(planConfigs.plan.useIntegrations);
          setShowSchedules(planConfigs.plan.useSchedules);
          setShowInternalChat(planConfigs.plan.useInternalChat);
          setShowExternalApi(planConfigs.plan.useExternalApi);
          setShowWavoipCall(planConfigs.plan.wavoip);
          setShowGroupManagement(planConfigs.plan.useGroupManagement);
          setShowPhraseListFlow(planConfigs.plan.usePhraseListFlow !== false);
          setShowConversationFlow(planConfigs.plan.useConversationFlow !== false);
          setShowFollowUp(planConfigs.plan.useFollowUp !== false);
        }
      } catch (error) {
        // Fallback
        setShowCampaigns(true);
        setShowKanban(true);
        setShowOpenAi(true);
        setShowIntegrations(true);
        setShowSchedules(true);
        setShowInternalChat(true);
        setShowExternalApi(true);
        setShowPhraseListFlow(true);
        setShowConversationFlow(true);
        setShowFollowUp(true);
      }
    }

    fetchData();
  }, [user?.companyId]);

  useEffect(() => {
    if (location.pathname.startsWith("/tickets")) {
      setActiveMenu("/tickets");
    } else {
      setActiveMenu("");
    }
  }, [location, setActiveMenu]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.id && socket && typeof socket.on === "function") {
      const companyId = user.companyId;
      const onCompanyChatMainListItems = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      socket.on(`company-${companyId}-chat`, onCompanyChatMainListItems);
      return () => {
        socket.off(`company-${companyId}-chat`, onCompanyChatMainListItems);
      };
    }
  }, [socket, user.id, user.companyId]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user.id]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  const getConnectionStatusClass = () => {
    if (!connectionWarning) return "success";

    const offlineWhats = whatsApps.filter((whats) => {
      return whats.status === "DISCONNECTED" || whats.status === "TIMEOUT";
    });

    if (offlineWhats.length > 0) {
      return "error";
    }

    return "warning";
  };

  const getConnectionStatusText = () => {
    const disconnectedCount = whatsApps.filter((whats) => {
      return (
        whats.status === "qrcode" ||
        whats.status === "PAIRING" ||
        whats.status === "DISCONNECTED" ||
        whats.status === "TIMEOUT" ||
        whats.status === "OPENING"
      );
    }).length;

    const connectedCount = whatsApps.length - disconnectedCount;

    if (disconnectedCount > 0) {
      return `${connectedCount}/${whatsApps.length}`;
    }

    return i18n.t("connections.check");
  };

  const getConnectionStatusIcon = () => {
    if (!connectionWarning) return <Wifi className="connection-icon" />;

    const offlineWhats = whatsApps.filter((whats) => {
      return whats.status === "DISCONNECTED" || whats.status === "TIMEOUT";
    });

    if (offlineWhats.length > 0) {
      return <WifiOff className="connection-icon" />;
    }

    return <AlertTriangle className="connection-icon" />;
  };

  const renderMenuItem = (to, text, icon, showBadge = false) => (
    <Link
      to={to}
      className="dropdown-menu-item"
      onClick={() => handleMenuItemClick(to)}
    >
      <div className="menu-item-icon">
        {showBadge ? (
          <div className="badge-container">
            {icon}
            <span className="badge">!</span>
          </div>
        ) : (
          icon
        )}
      </div>
      <span className="menu-item-text">{text}</span>
      {to === "/connections" && connectionWarning && (
        <Tooltip
          title={i18n.t("connections.title") + ": " + getConnectionStatusText()}
          placement="right"
          arrow
        >
          <div className={`connection-status ${getConnectionStatusClass()}`}>
            {getConnectionStatusIcon()}
            <span>{getConnectionStatusText()}</span>
          </div>
        </Tooltip>
      )}
    </Link>
  );

  return (
    <div
      className={`main-list-items ${isMobile ? "mobile" : ""} ${isDarkMode ? "dark" : ""
        }`}
    >
      {/* GESTÃO */}
      <CanWrapper
        role={
          (user.profile === "user" && user.showDashboard === "enabled") ||
            user.allowRealTime === "enabled"
            ? "admin"
            : user.profile
        }
        perform={"drawer-admin-items:view"}
      >
        <div className="dropdown-container">
          <button
            ref={managementButtonRef}
            className={`nav-button ${isManagementActive ? "active" : ""}`}
            onClick={toggleManagementMenu}
          >
            <Home className="nav-button-icon" />
            <span>{i18n.t("mainDrawer.listItems.management")}</span>
            {isMobile && (
              <ChevronDown
                className={`dropdown-icon ${managementMenuOpen ? "open" : ""}`}
              />
            )}
          </button>

          <div
            ref={managementMenuRef}
            className={`dropdown-menu ${isMobile && managementMenuOpen ? "open" : ""
              }`}
          >
            <Can
              role={
                user.profile === "user" && user.showDashboard === "enabled"
                  ? "admin"
                  : user.profile
              }
              perform={"drawer-admin-items:view"}
              yes={() => (
                <>
                  {renderMenuItem(
                    "/",
                    "Dashboard",
                    <BarChart3 className="hero-icon" />
                  )}
                  {renderMenuItem(
                    "/reports",
                    i18n.t("mainDrawer.listItems.reports"),
                    <FileText className="hero-icon" />
                  )}
                </>
              )}
            />

            <Can
              role={
                user.profile === "user" && user.allowRealTime === "enabled"
                  ? "admin"
                  : user.profile
              }
              perform={"drawer-admin-items:view"}
              yes={() =>
                renderMenuItem(
                  "/moments",
                  i18n.t("mainDrawer.listItems.chatsTempoReal"),
                  <Table className="hero-icon" />
                )
              }
            />

            {user.profile === "admin" &&
              showFollowUp &&
              renderMenuItem(
                "/plugins/floup/dashboard",
                "Painel FollowUP",
                <Columns className="hero-icon" />
              )}

            {user.profile === "admin" &&
              showWallets &&
              renderMenuItem(
                "/wallets",
                i18n.t("mainDrawer.listItems.wallets"),
                <Wallet className="hero-icon" />
              )}
          </div>
        </div>
      </CanWrapper>

      {/* ATENDIMENTO */}
      <div className="dropdown-container">
        <button
          ref={ticketsButtonRef}
          className={`nav-button ${isTicketsActive ? "active" : ""}`}
          onClick={toggleTicketsMenu}
        >
          <MessageSquare className="nav-button-icon" />
          <span>{i18n.t("mainDrawer.listItems.tickets")}</span>
          {isMobile && (
            <ChevronDown
              className={`dropdown-icon ${ticketsMenuOpen ? "open" : ""}`}
            />
          )}
        </button>

        <div
          ref={ticketsMenuRef}
          className={`dropdown-menu ${isMobile && ticketsMenuOpen ? "open" : ""
            }`}
        >
          {renderMenuItem(
            "/tickets",
            i18n.t("mainDrawer.listItems.tickets"),
            <MessagesSquare className="hero-icon" />
          )}
          {renderMenuItem(
            "/browser-notifications",
            "Notificações do navegador",
            <Bell className="hero-icon" />
          )}
          {renderMenuItem(
            "/quick-messages",
            i18n.t("mainDrawer.listItems.quickMessages"),
            <Zap className="hero-icon" />
          )}
          {renderMenuItem(
            "/whatsapp-templates",
            "Templates Meta",
            <FileText className="hero-icon" />
          )}
          {showWavoipCall &&
            renderMenuItem(
              "/call-historicals",
              i18n.t("mainDrawer.listItems.callHistorical"),
              <Phone className="hero-icon" />
            )}
          {user?.allowWhatsappStatus === "enabled" &&
            renderMenuItem("/status", "Status", <Image className="hero-icon" />)}
          {showKanban &&
            renderMenuItem(
              "/kanban",
              i18n.t("mainDrawer.listItems.kanban"),
              <Columns className="hero-icon" />
            )}
          {showKanbanEnterprise &&
            renderMenuItem(
              "/kanban-enterprise",
              "Kanban Enterprise",
              <Columns className="hero-icon" />
            )}
          {user.showContacts === "enabled" &&
            renderMenuItem(
              "/contacts",
              i18n.t("mainDrawer.listItems.contacts"),
              <User className="hero-icon" />
            )}
          {showSchedules &&
            renderMenuItem(
              "/schedules",
              i18n.t("mainDrawer.listItems.schedules"),
              <Clock className="hero-icon" />
            )}
          {renderMenuItem(
            "/tags",
            i18n.t("mainDrawer.listItems.tags"),
            <Tag className="hero-icon" />
          )}
          {user.profile === "admin" &&
            renderMenuItem(
              "/custom-contact-fields",
              i18n.t("mainDrawer.listItems.customContactFields"),
              <SlidersHorizontal className="hero-icon" />
            )}
          {showInternalChat &&
            renderMenuItem(
              "/chats",
              i18n.t("mainDrawer.listItems.chats"),
              <div className={`notification-icon ${invisible ? "" : "has-notification"}`}>
                <MessageCircle className="hero-icon" />
              </div>
            )}
          {hasHelps &&
            renderMenuItem(
              "/helps",
              i18n.t("mainDrawer.listItems.helps"),
              <HelpCircle className="hero-icon" />
            )}
        </div>
      </div>

      {/* CAMPANHAS - visível se plano tem campanhas e (não é user OU user com showCampaign enabled). Sem CanWrapper para não bloquear usuários com perfil "user" que têm permissão de campanha. */}
      {showCampaigns && (user?.profile !== "user" || user?.showCampaign === "enabled") && (
        <div className="dropdown-container">
          <button
            ref={campaignsButtonRef}
            className={`nav-button ${isCampaignRouteActive ? "active" : ""}`}
            onClick={toggleCampaignsMenu}
          >
            <Megaphone className="nav-button-icon" />
            <span>{i18n.t("mainDrawer.listItems.campaigns")}</span>
            {isMobile && (
              <ChevronDown
                className={`dropdown-icon ${campaignsMenuOpen ? "open" : ""}`}
              />
            )}
          </button>

          <div
            ref={campaignsMenuRef}
            className={`dropdown-menu ${isMobile && campaignsMenuOpen ? "open" : ""
              }`}
          >
            {renderMenuItem(
              "/campaigns",
              i18n.t("campaigns.subMenus.list"),
              <List className="hero-icon" />
            )}
            {renderMenuItem(
              "/contact-lists",
              i18n.t("campaigns.subMenus.listContacts"),
              <UsersRound className="hero-icon" />
            )}
            {renderMenuItem(
              "/campaigns-config",
              i18n.t("campaigns.subMenus.settings"),
              <Settings className="hero-icon" />
            )}
            {renderMenuItem(
              "/official-campaigns",
              "Campanhas Oficiais",
              <Megaphone className="hero-icon" />
            )}
            {renderMenuItem(
              "/campaigns-pro",
              "Campanhas PRO",
              <Megaphone className="hero-icon" />
            )}
          </div>
        </div>
      )}

      {/* AUTOMAÇÕES — plano + permissão showFlow */}
      {(user?.profile !== "user" || user?.showFlow === "enabled") &&
        (showPhraseListFlow || showConversationFlow || showFollowUp) && (
        <div className="dropdown-container">
          <button
            ref={flowButtonRef}
            className={`nav-button ${isFlowRouteActive ? "active" : ""}`}
            onClick={toggleFlowMenu}
          >
            <Webhook className="nav-button-icon" />
            <span>{i18n.t("mainDrawer.listItems.automations")}</span>
            {isMobile && (
              <ChevronDown
                className={`dropdown-icon ${flowMenuOpen ? "open" : ""}`}
              />
            )}
          </button>

          <div
            ref={flowMenuRef}
            className={`dropdown-menu ${isMobile && flowMenuOpen ? "open" : ""}`}
          >
            {showPhraseListFlow &&
              renderMenuItem(
                "/phrase-lists",
                "Fluxo de Campanha",
                <Calendar className="hero-icon" />
              )}
            {showConversationFlow &&
              renderMenuItem(
                "/flowbuilders",
                "Fluxo de conversa",
                <Workflow className="hero-icon" />
              )}
            {showFollowUp &&
              renderMenuItem(
                "/plugins/floup",
                "Follow UP (Templates)",
                <Webhook className="hero-icon" />
              )}
          </div>
        </div>
      )}

      {/* ADMINISTRAÇÃO */}
      <CanWrapper
        role={
          user.profile === "user" && user.allowConnections === "enabled"
            ? "admin"
            : user.profile
        }
        perform="dashboard:view"
      >
        <div className="dropdown-container">
          <button
            ref={adminButtonRef}
            className={`nav-button ${isAdminActive ? "active" : ""}`}
            onClick={toggleAdminMenu}
          >
            <Cog className="nav-button-icon" />
            <span>{i18n.t("mainDrawer.listItems.administration")}</span>
            {isMobile && (
              <ChevronDown
                className={`dropdown-icon ${adminMenuOpen ? "open" : ""}`}
              />
            )}
          </button>
          <div
            ref={adminMenuRef}
            className={`dropdown-menu ${isMobile && adminMenuOpen ? "open" : ""
              }`}
          >
            {user.super &&
              renderMenuItem(
                "/announcements",
                i18n.t("mainDrawer.listItems.annoucements"),
                <Info className="hero-icon" />
              )}
            {showExternalApi && (
              <>
                <Can
                  role={user.profile}
                  perform="dashboard:view"
                  yes={() =>
                    renderMenuItem(
                      "/messages-api",
                      i18n.t("mainDrawer.listItems.messagesAPI"),
                      <Code className="hero-icon" />
                    )
                  }
                />
                <Can
                  role={user.profile}
                  perform="dashboard:view"
                  yes={() =>
                    renderMenuItem(
                      "/api-oficial-docs",
                      i18n.t("mainDrawer.listItems.apiOficialDocs"),
                      <Code className="hero-icon" />
                    )
                  }
                />
              </>
            )}
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() =>
                renderMenuItem(
                  "/test-interactive",
                  i18n.t("mainDrawer.listItems.testInteractive"),
                  <Code className="hero-icon" />
                )
              }
            />
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() =>
                renderMenuItem(
                  "/users",
                  i18n.t("mainDrawer.listItems.users"),
                  <Users className="hero-icon" />
                )
              }
            />
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() =>
                renderMenuItem(
                  "/birthday-settings",
                  i18n.t("mainDrawer.listItems.birthdaySettings"),
                  <Cake className="hero-icon" />
                )
              }
            />
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() =>
                renderMenuItem(
                  "/holidays",
                  i18n.t("holidays.title"),
                  <Calendar className="hero-icon" />
                )
              }
            />
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() =>
                renderMenuItem(
                  "/queues",
                  i18n.t("mainDrawer.listItems.queues"),
                  <ListOrdered className="hero-icon" />
                )
              }
            />
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() =>
                renderMenuItem(
                  "/files",
                  i18n.t("mainDrawer.listItems.files"),
                  <Paperclip className="hero-icon" />
                )
              }
            />
            {showOpenAi && (
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() =>
                  renderMenuItem(
                    "/prompts",
                    i18n.t("mainDrawer.listItems.prompts"),
                    <Sparkles className="hero-icon" />
                  )
                }
              />
            )}
            {showIntegrations && (
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() =>
                  renderMenuItem(
                    "/queue-integration",
                    i18n.t("mainDrawer.listItems.queueIntegration"),
                    <Cable className="hero-icon" />
                  )
                }
              />
            )}
            <Can
              role={
                user.profile === "user" && user.allowConnections === "enabled"
                  ? "admin"
                  : user.profile
              }
              perform={"drawer-admin-items:view"}
              yes={() =>
                renderMenuItem(
                  "/connections",
                  i18n.t("mainDrawer.listItems.connections"),
                  <ArrowLeftRight className="hero-icon" />,
                  connectionWarning
                )
              }
            />
            {user.super &&
              renderMenuItem(
                "/allConnections",
                i18n.t("mainDrawer.listItems.allConnections"),
                <Server className="hero-icon" />
              )}
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() =>
                renderMenuItem(
                  "/financeiro",
                  i18n.t("mainDrawer.listItems.financeiro"),
                  <DollarSign className="hero-icon" />
                )
              }
            />
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() =>
                renderMenuItem(
                  "/settings",
                  i18n.t("mainDrawer.listItems.settings"),
                  <Settings className="hero-icon" />
                )
              }
            />
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() =>
                renderMenuItem(
                  "/audit-logs",
                  i18n.t("mainDrawer.listItems.auditLogs"),
                  <History className="hero-icon" />
                )
              }
            />
            {user?.super &&
              renderMenuItem(
                "/companies",
                i18n.t("mainDrawer.listItems.companies"),
                <Building2 className="hero-icon" />
              )}

            {user?.super &&
              renderMenuItem(
                "/super-admin",
                "Acessar Empresas",
                <UsersRound className="hero-icon" />
              )}



          </div>
        </div>
      </CanWrapper>
    </div>
  );
};

export default MainListItems;
